"use client";

import { cn } from "@newxsystems/shared";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@templates/design-system";
import { useEffect, useId, useState, type CSSProperties } from "react";
import { resolveTootPrimitive } from "../../../../client";
import { RichTextHtml } from "../../../../cms/components/RichTextHtml";
import { TootActions } from "../../../layout/components/TootActions/TootActions";
import type { ITootImageCarouselProps } from "./TootImageCarousel.props";

const formatIndex = (value: number) => String(value).padStart(2, "0");

export const TootImageCarousel = ({ behavior, className, items }: ITootImageCarouselProps) => {
  const Image = resolveTootPrimitive("Image");
  const carouselId = useId();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [autoplayAllowed, setAutoplayAllowed] = useState(false);
  const slides = items ?? [];
  const loop = behavior?.loop === true;
  const snapCount = count || slides.length;
  const active = Math.min(current + 1, snapCount || 1);
  const autoplayConfigured = Boolean(behavior?.autoplay);
  const autoplayActive = autoplayAllowed && !paused && !interacting;

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  useEffect(() => {
    if (!behavior?.autoplay) {
      setAutoplayAllowed(false);
      return;
    }
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setAutoplayAllowed(!query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [behavior?.autoplay]);

  useEffect(() => {
    if (!api || !autoplayActive) return;
    const timer = window.setInterval(() => {
      if (!loop && !api.canScrollNext()) {
        setPaused(true);
        return;
      }
      api.scrollNext();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [api, autoplayActive, loop]);

  if (!slides.length) return null;

  return (
    <Carousel
      aria-label="Image carousel"
      className={cn("relative w-full", className)}
      onBlurCapture={(event) => {
        const next = event.relatedTarget;
        if (!(next instanceof Node) || !event.currentTarget.contains(next)) setInteracting(false);
      }}
      onFocusCapture={() => setInteracting(true)}
      onMouseEnter={() => setInteracting(true)}
      onMouseLeave={() => setInteracting(false)}
      opts={{ align: "start", loop }}
      setApi={setApi}
    >
      <p aria-live={autoplayActive ? "off" : "polite"} className="sr-only">
        Showing carousel position {active} of {snapCount || slides.length}.
      </p>
      <div className="relative overflow-hidden rounded-[var(--toot-radius-card)] border border-toot-grid bg-muted p-2 [contain:layout_paint] sm:p-3 lg:p-4">
        <CarouselContent
          className="ml-0 gap-x-4 will-change-transform"
          viewportClassName="relative rounded-[calc(var(--toot-radius-card)-0.2rem)] [contain:layout_paint]"
        >
          {slides.map((item, index) => {
            const image = item.image;
            const width = image.width ?? item.width;
            const height = image.height ?? item.height;
            const style: CSSProperties | undefined = image.focalPoint
              ? { objectPosition: `${image.focalPoint.x * 100}% ${image.focalPoint.y * 100}%` }
              : undefined;
            return (
              <CarouselItem
                className="basis-full pl-0"
                id={`${carouselId}-slide-${index + 1}`}
                key={`${image.defaultSrc}-${index}`}
              >
                <figure className="relative h-full min-w-0 overflow-hidden rounded-[calc(var(--toot-radius-card)-0.2rem)] border border-toot-grid bg-card lg:grid lg:grid-cols-[minmax(0,1.48fr)_minmax(19rem,0.62fr)]">
                  <div
                    className="relative min-h-[220px] overflow-hidden bg-muted sm:min-h-[300px] lg:min-h-[420px]"
                    style={{ aspectRatio: item.aspectRatio ?? undefined }}
                  >
                    <Image
                      alt={image.alternateText}
                      className="h-full w-full"
                      height={height ?? undefined}
                      imageClassName="h-full w-full !object-cover"
                      loading={index === 0 ? "eager" : "lazy"}
                      sizes={
                        image.sizes ?? "(min-width: 1280px) 46rem, (min-width: 1024px) 62vw, 100vw"
                      }
                      src={image.defaultSrc}
                      srcSet={image.srcSet ?? undefined}
                      style={style}
                      width={width ?? undefined}
                    />
                  </div>
                  <figcaption className="flex min-w-0 flex-1 flex-col gap-4 p-5 sm:p-6 lg:p-7">
                    <div className="flex flex-wrap items-center gap-2">
                      {item.category ? (
                        <span className="rounded-full bg-primary-100 px-3 py-1 text-[0.68rem] font-black uppercase tracking-[0.16em] text-primary-700">
                          {item.category}
                        </span>
                      ) : null}
                      {(item.tags ?? []).map((tag, tagIndex) => (
                        <span
                          className="rounded-full border border-toot-grid bg-card px-3 py-1 text-xs font-bold text-toot-muted"
                          key={`${tag.tag}-${tagIndex}`}
                        >
                          {tag.tag}
                        </span>
                      ))}
                    </div>
                    {item.caption ? (
                      <p className="text-pretty text-2xl font-black leading-[1.08] tracking-[-0.03em] text-toot-foreground lg:text-[1.7rem]">
                        {item.caption}
                      </p>
                    ) : null}
                    {item.content ? (
                      <RichTextHtml
                        className="text-pretty text-base leading-7 text-toot-muted [&_a]:font-black [&_a]:text-toot-orange [&_a]:underline [&_li]:ml-5 [&_li]:list-disc [&_p+p]:mt-3"
                        html={item.content}
                      />
                    ) : null}
                    {item.buttons?.length ? <TootActions actions={item.buttons} /> : null}
                    {item.credit ? (
                      <p className="mt-auto border-t border-toot-grid pt-4 text-sm font-bold text-toot-muted">
                        {item.credit}
                      </p>
                    ) : null}
                  </figcaption>
                </figure>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </div>

      {(behavior?.controls === true && snapCount > 1) || autoplayConfigured ? (
        <div className="mt-5 grid gap-4 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center">
          {behavior?.controls === true && snapCount > 1 ? (
            <div className="flex items-center justify-center gap-3 lg:justify-start">
              <button
                aria-label="Show previous carousel image"
                className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-full border border-toot-grid bg-card text-toot-orange shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
                disabled={!loop && !canPrev}
                onClick={() => api?.scrollPrev()}
                type="button"
              >
                ←
              </button>
              <button
                aria-label="Show next carousel image"
                className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-full bg-toot-red text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
                disabled={!loop && !canNext}
                onClick={() => api?.scrollNext()}
                type="button"
              >
                →
              </button>
            </div>
          ) : (
            <div />
          )}
          {snapCount > 1 ? (
            <div
              className="flex items-center justify-center gap-2"
              role="group"
              aria-label="Choose carousel slide"
            >
              {Array.from({ length: snapCount }, (_, index) => (
                <button
                  aria-current={index === current ? "true" : undefined}
                  aria-label={`Go to carousel image ${index + 1}`}
                  className={cn(
                    "h-2.5 cursor-pointer rounded-full transition-[width,background-color]",
                    index === current
                      ? "w-8 bg-toot-red"
                      : "w-2.5 bg-toot-grid-strong hover:bg-toot-orange",
                  )}
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  type="button"
                />
              ))}
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center justify-center gap-3 text-sm font-black text-toot-muted lg:justify-end">
            <span>
              {formatIndex(active)} / {formatIndex(snapCount || slides.length)}
            </span>
            {autoplayConfigured ? (
              <button
                aria-label={paused ? "Resume carousel autoplay" : "Pause carousel autoplay"}
                className="min-h-11 cursor-pointer rounded-full border border-toot-grid bg-card px-4 text-xs font-black uppercase tracking-[0.12em] text-toot-foreground"
                disabled={!autoplayAllowed}
                onClick={() => setPaused((value) => !value)}
                type="button"
              >
                {paused ? "Play" : "Pause"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </Carousel>
  );
};

TootImageCarousel.displayName = "Interactive::TootImageCarousel";
